/**
 * Academy Flip Pille – scroll-gebundener S-Kurven-Flug.
 *
 * Selektoren (fest):
 *   [data-flip-pill-root]  – die Flip-Pille-Section. Rendert die Pille und trägt
 *       die Startdaten:
 *         data-start-ref  … Section-ID / #Anker / CSS-Selektor der Start-Referenz
 *                           (leer = relativ zum Viewport)
 *         data-start-x/y  … Startposition in % innerhalb der Referenz bzw. des
 *                           Viewports
 *         data-curve      … Stärke der seitlichen S-Kurve (0–100)
 *   [data-flip-section]    – die feste Ziel-Section (immer dieselbe, „Academy
 *                            Mitwachsend").
 *   [data-flip-slot]       – der feste Ziel-Container darin; hier landet die Pille.
 *   [data-flip-me]         – die Pille selbst (Snippet academy-flip-pill).
 *
 * Ablauf: GSAP ScrollTrigger mit scrub, getriggert von der Ziel-Section. Über
 * deren Scroll-Strecke läuft die Pille (position: fixed) an einer kubischen
 * Bézier-S-Kurve vom Startpunkt in die Slot-Position; in der ersten Phase
 * wächst sie und die Zahl zählt 1–5 durch. Bei vollem Fortschritt wird sie in
 * [data-flip-slot] eingehängt und sitzt normal darin.
 *
 * Nichts-tun / Fallback (ohne Fehler):
 *   - Keine Pille oder kein [data-flip-slot] vorhanden.
 *   - prefers-reduced-motion oder GSAP/ScrollTrigger fehlt → Pille direkt in den
 *     Slot, Zahl auf Zielwert, keine Animation.
 *
 * Theme-Editor: bei shopify:section:load / shopify:section:unload wird alles neu
 * aufgebaut; eine bereits gelandete Pille wird dabei zurückgeholt.
 */
if (!window.__academyFlipInit) {
  window.__academyFlipInit = true;

  (function () {
    'use strict';

    var ROOT_SEL    = '[data-flip-pill-root]';
    var SECTION_SEL = '[data-flip-section]';
    var SLOT_SEL    = '[data-flip-slot]';
    var PILL_SEL    = '[data-flip-me]';
    var NUM_SEL     = '[data-flip-pill-num]';

    var GROW_END = 0.4;   // Fortschritt, ab dem die Pille voll gewachsen ist

    var reduceMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var instances = [];   // { root, pill, st }

    function hasGsap() { return typeof window.gsap !== 'undefined'; }
    function hasST()   { return typeof window.ScrollTrigger !== 'undefined'; }
    function nnum(v, d) { var n = parseFloat(v); return isNaN(n) ? d : n; }

    function targetNumber(el) {
      var n = parseInt(el.getAttribute('data-number'), 10);
      return (n >= 1 && n <= 5) ? n : 5;
    }

    function setNumber(pill, v) {
      var n = pill.querySelector(NUM_SEL);
      if (n) n.textContent = v;
    }

    /* Fester Ziel-Slot – bevorzugt der innerhalb der Ziel-Section. */
    function findSlot() {
      return document.querySelector(SECTION_SEL + ' ' + SLOT_SEL) ||
             document.querySelector(SLOT_SEL);
    }

    /* Start-Referenz auflösen: Section-ID, #Anker oder roher CSS-Selektor.
       Liegt der Treffer in einer Shopify-Section, wird die ganze Section
       genommen. Ungültig / leer → null (dann Viewport-relativ). */
    function resolveRef(ref) {
      if (!ref) return null;
      ref = String(ref).trim();
      if (!ref) return null;
      var el = null;
      var asId = ref.charAt(0) === '#' ? ref : '#' + ref;
      try { el = document.querySelector(asId); } catch (e) {}
      if (!el) { try { el = document.querySelector(ref); } catch (e) {} }
      if (!el) return null;
      return el.closest('.shopify-section') || el;
    }

    function cubic(p, a, b, c, d) {
      var mt = 1 - p;
      return mt * mt * mt * a + 3 * mt * mt * p * b + 3 * mt * p * p * c + p * p * p * d;
    }
    function smooth(t) { return t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t); }

    /* Pille ohne Animation in den Slot – Fallback / reduzierte Bewegung. */
    function settleStatic(pill, slot) {
      if (pill.parentNode !== slot) slot.appendChild(pill);
      if (window.gsap) window.gsap.set(pill, { clearProps: 'all' });
      else pill.style.position = '';
      setNumber(pill, targetNumber(pill));
    }

    function buildInstance(root) {
      var pill = root.querySelector(PILL_SEL) || root.__flipPill;
      if (!pill) return;
      root.__flipPill = pill;

      var slot = findSlot();
      if (!slot) return;                       // kein Ziel → Pille bleibt stehen.

      // Pille aus einem früheren Lauf ggf. zurück in ihre Section holen.
      if (!root.contains(pill)) {
        if (window.gsap) window.gsap.set(pill, { clearProps: 'all' });
        root.appendChild(pill);
      }

      if (!hasGsap() || !hasST() || reduceMotion) {
        settleStatic(pill, slot);
        return;
      }

      var gsap = window.gsap;
      var target = targetNumber(pill);
      var targetSection = slot.closest(SECTION_SEL) || slot;

      var pillRect = pill.getBoundingClientRect();
      var pillW = pillRect.width || nnum(getComputedStyle(pill).width, 120);
      var pillH = pillRect.height || pillW * 1.513;

      var pts = null;      // { s, c1, c2, e } in Dokument-Koordinaten
      var curveVal = 40;
      var settled = false;

      function measure() {
        curveVal = nnum(root.getAttribute('data-curve'), 40);
        var targetTop = targetSection.getBoundingClientRect().top + window.scrollY;

        // Endpunkt = Pillen-Mitte, wenn sie oben links im Slot sitzt (so wie
        // sie nach dem Einhängen per Normal-Flow tatsächlich landet).
        var sr = slot.getBoundingClientRect();
        var e = {
          x: sr.left + window.scrollX + pillW / 2,
          y: sr.top + window.scrollY + pillH / 2
        };

        // Startpunkt.
        var sx = nnum(root.getAttribute('data-start-x'), 50);
        var sy = nnum(root.getAttribute('data-start-y'), 12);
        var refEl = resolveRef(root.getAttribute('data-start-ref'));
        var s;
        if (refEl) {
          var rr = refEl.getBoundingClientRect();
          s = {
            x: rr.left + window.scrollX + (sx / 100) * rr.width,
            y: rr.top + window.scrollY + (sy / 100) * rr.height
          };
        } else {
          // Viewport-relativ, verankert an der Scroll-Position bei Flug-Beginn
          // (ScrollTrigger-start "top bottom").
          var scrollAtStart = targetTop - window.innerHeight;
          s = {
            x: (sx / 100) * window.innerWidth,
            y: scrollAtStart + (sy / 100) * window.innerHeight
          };
        }

        // Zwei Kontrollpunkte, je auf einer Seite der Verbindungslinie → S-Kurve.
        var dx = e.x - s.x, dy = e.y - s.y;
        var len = Math.sqrt(dx * dx + dy * dy) || 1;
        var nx = -dy / len, ny = dx / len;             // Normale
        var k = (curveVal / 100) * Math.min(280, len * 0.5);

        pts = {
          s: s,
          c1: { x: s.x + dx * 0.33 + nx * k, y: s.y + dy * 0.33 + ny * k },
          c2: { x: s.x + dx * 0.66 - nx * k, y: s.y + dy * 0.66 - ny * k },
          e: e
        };
      }

      function toSlot() {
        if (settled) return;
        settled = true;
        if (pill.parentNode !== slot) slot.appendChild(pill);
        gsap.set(pill, { clearProps: 'all' });
        setNumber(pill, target);
      }

      function toFlight() {
        settled = false;
        if (pill.parentNode !== root) root.appendChild(pill);
      }

      function render(p) {
        if (!pts) measure();

        if (p >= 0.999) { toSlot(); return; }
        toFlight();

        var ease = smooth(p);
        var docX = cubic(ease, pts.s.x, pts.c1.x, pts.c2.x, pts.e.x);
        var docY = cubic(ease, pts.s.y, pts.c1.y, pts.c2.y, pts.e.y);
        var grow = smooth(Math.min(1, p / GROW_END));
        var wob  = 6 + (curveVal / 100) * 8;

        gsap.set(pill, {
          position: 'fixed',
          left: docX - window.scrollX,
          top: docY - window.scrollY,
          xPercent: -50,
          yPercent: -50,
          margin: 0,
          scale: 0.12 + 0.88 * grow,
          rotation: Math.sin(p * Math.PI) * -wob,
          opacity: p < 0.015 ? 0 : 1,
          zIndex: 40
        });

        setNumber(pill, p >= GROW_END
          ? target
          : (Math.floor((p / GROW_END) * 14) % 5) + 1);
      }

      gsap.set(pill, { opacity: 0 });

      var st = window.ScrollTrigger.create({
        trigger: targetSection,
        start: 'top bottom',
        end: 'top center',
        scrub: 0.6,
        onRefresh: measure,
        onUpdate: function (self) { render(self.progress); }
      });

      instances.push({ root: root, pill: pill, st: st });
      render(0);
    }

    function teardown() {
      for (var i = 0; i < instances.length; i++) {
        var inst = instances[i];
        try { inst.st && inst.st.kill(); } catch (e) {}
        if (inst.pill && inst.root && !inst.root.contains(inst.pill)) {
          if (window.gsap) window.gsap.set(inst.pill, { clearProps: 'all' });
          inst.root.appendChild(inst.pill);
        }
      }
      instances.length = 0;
    }

    function build() {
      teardown();
      var roots = document.querySelectorAll(ROOT_SEL);
      for (var i = 0; i < roots.length; i++) buildInstance(roots[i]);
      if (hasST()) window.ScrollTrigger.refresh();
    }

    function init() {
      build();
      window.addEventListener('load', function () {
        if (hasST()) window.ScrollTrigger.refresh();
      });
      document.addEventListener('shopify:section:load', build);
      document.addEventListener('shopify:section:unload', build);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  })();
}
